import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type { ApiErrorDetail } from '@klareospace/pharos-lib-types';
import { getApiErrorDetail } from '@klareospace/pharos-lib-front-utils';
import type { TFunction } from 'i18next';
import { toast } from 'sonner';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CreateMutationCallbacksOptions<TVariables = void> {
  /** i18n key for the error toast, or a function of the variables. Omit / return undefined to suppress the error toast. */
  errorKey?: string | ((variables: TVariables) => string | undefined);
  /** Extra side-effect on error; receives the extracted detail and variables. Runs before the error toast. */
  onError?: (detail: ApiErrorDetail, variables: TVariables) => void;
  /** Extra side-effect on success (e.g. close a drawer). Receives variables. Runs before the success toast. */
  onSuccess?: (variables: TVariables) => void;
  /** i18n key for the success toast, or a function of the variables. Omit / return undefined to suppress the success toast (silent mutations). */
  successKey?: string | ((variables: TVariables) => string | undefined);
  /** Query keys to invalidate on success: an array, or a function of the variables returning an array. */
  invalidateKeys?: QueryKey[] | ((variables: TVariables) => QueryKey[]);
  /** Required only when `invalidateKeys` is used. */
  queryClient?: QueryClient;
  /** Required only when `errorKey` or `successKey` is used. When omitted, the raw key is shown as-is. */
  t?: TFunction;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveKey<TVariables>(
  key: string | ((variables: TVariables) => string | undefined) | undefined,
  variables: TVariables,
): string | undefined {
  return typeof key === 'function' ? key(variables) : key;
}

// ─── Factory ──────────────────────────────────────────────────────────────────

export function createMutationCallbacks<TData = unknown, TVariables = void>(
  opts: CreateMutationCallbacksOptions<TVariables>,
): {
  onError: (error: unknown, variables: TVariables) => void;
  onSuccess: (data: TData, variables: TVariables) => void;
} {
  return {
    onError: (error: unknown, variables: TVariables): void => {
      const detail = getApiErrorDetail(error);
      opts.onError?.(detail, variables);
      const errorKey = resolveKey(opts.errorKey, variables);
      if (errorKey) {
        toast.error(detail.message || (opts.t?.(errorKey) ?? errorKey));
      }
    },
    onSuccess: (_data: TData, variables: TVariables): void => {
      const keys = typeof opts.invalidateKeys === 'function' ? opts.invalidateKeys(variables) : opts.invalidateKeys;
      if (keys) {
        for (const queryKey of keys) {
          void opts.queryClient?.invalidateQueries({ queryKey });
        }
      }
      opts.onSuccess?.(variables);
      const successKey = resolveKey(opts.successKey, variables);
      if (successKey) {
        toast.success(opts.t?.(successKey) ?? successKey);
      }
    },
  };
}
